import { useMemo } from 'react';
import type { Coupon, SpinRecord } from '@shared';
import { useNavigation } from '../app/navigation';
import { FulfilmentBadge } from '../components/features/FulfilmentBadge';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Icon } from '../components/ui/Icon';
import { Panel } from '../components/ui/Panel';
import { StatTile } from '../components/ui/StatTile';
import { useGame } from '../game/store';
import { formatRelative } from '../lib/format';
import layout from '../styles/layout.module.css';
import styles from './RewardsPage.module.css';

/**
 * What the player won, all in one place.
 *
 * Two kinds of thing land here. **Deliverables** — a role or a custom prize — are
 * spins that carry a `status`, because winning one leaves a human something to
 * hand over. **Coupons** settle on the spot and need no delivery, but they were
 * still *won*, so a player looking for "what did I get" should find them here too
 * rather than only on the Earn page. Credits and blanks are not shown: a credit
 * is already in the balance, and a blank won nothing.
 */
function RewardRow({ spin }: { spin: SpinRecord }) {
  return (
    <li className={styles.reward}>
      <span className={styles.dot} style={{ background: spin.color }} />
      <span className={styles.body}>
        <span className={styles.name}>{spin.name}</span>
        <span className={styles.meta}>
          Won {formatRelative(spin.at)}
          {spin.note ? ` · ${spin.note}` : ''}
        </span>
      </span>
      {spin.status && <FulfilmentBadge status={spin.status} />}
    </li>
  );
}

function CouponRow({ coupon }: { coupon: Coupon }) {
  const used = Boolean(coupon.usedAt);
  return (
    <li className={`${styles.reward} ${used ? styles.spent : ''}`}>
      <span className={styles.couponPercent}>−{coupon.percent}%</span>
      <span className={styles.body}>
        <span className={styles.name}>{coupon.label}</span>
        <span className={styles.meta}>
          {coupon.source === 'spin' ? 'Won' : 'Given'} {formatRelative(coupon.createdAt)}
        </span>
      </span>
      {used ? (
        <Badge tone="neutral">Used</Badge>
      ) : (
        <code className={styles.couponCode}>{coupon.code}</code>
      )}
    </li>
  );
}

export function RewardsPage() {
  const { spins, coupons } = useGame();
  const { navigate } = useNavigation();

  const deliverable = useMemo(() => spins.filter((spin) => spin.status !== undefined), [spins]);

  const open = deliverable.filter(
    (spin) => spin.status === 'pending' || spin.status === 'accepted',
  );
  const done = deliverable.filter((spin) => spin.status === 'delivered');
  const cancelled = deliverable.filter((spin) => spin.status === 'cancelled');
  const usableCoupons = coupons.filter((coupon) => !coupon.usedAt);

  if (deliverable.length === 0 && coupons.length === 0) {
    return (
      <EmptyState
        icon="gift"
        title="No rewards yet"
        description="Prizes you win on a drop show up here — coupons the moment you win them, and anything the team hands over once it's on its way."
        action={
          <Button variant="primary" icon="wheel" onClick={() => navigate('wheel')}>
            Try the Lucky Drop
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className={layout.grid}>
        <StatTile
          icon="clock"
          accent="gold"
          label="Waiting"
          value={`${open.length}`}
          hint="being handled"
        />
        <StatTile
          icon="check"
          accent="mint"
          label="Delivered"
          value={`${done.length}`}
          hint="already yours"
        />
        <StatTile
          icon="ticket"
          accent="brand"
          label="Coupons"
          value={`${usableCoupons.length}`}
          hint="ready to use"
        />
      </div>

      {coupons.length > 0 && (
        <Panel
          title="Coupons"
          subtitle="Won on a drop or handed to you — stackable, single use"
          actions={
            <Badge tone={usableCoupons.length ? 'brand' : 'neutral'}>
              {usableCoupons.length} available
            </Badge>
          }
        >
          <ul className={styles.list}>
            {coupons.map((coupon) => (
              <CouponRow key={coupon.id} coupon={coupon} />
            ))}
          </ul>
          <p className={styles.hint}>
            Quote a code on the creator&apos;s page when you buy — it comes off there, not inside
            Spoyl.
          </p>
        </Panel>
      )}

      <Panel
        title="Pending"
        subtitle="Won, and on its way"
        actions={<Icon name="clock" size={16} />}
      >
        {open.length === 0 ? (
          <p className={layout.sectionHint}>Nothing waiting — everything has been handed over.</p>
        ) : (
          <>
            <ul className={styles.list}>
              {open.map((spin) => (
                <RewardRow key={spin.id} spin={spin} />
              ))}
            </ul>
            <p className={styles.hint}>
              Nothing to do — the team sees everything waiting here and will be in touch to hand it
              over.
            </p>
          </>
        )}
      </Panel>

      <Panel title="Delivered" subtitle="Already handed over">
        {done.length === 0 ? (
          <p className={layout.sectionHint}>Nothing delivered yet.</p>
        ) : (
          <ul className={styles.list}>
            {done.map((spin) => (
              <RewardRow key={spin.id} spin={spin} />
            ))}
          </ul>
        )}
      </Panel>

      {cancelled.length > 0 && (
        <Panel title="Cancelled" subtitle="Closed without delivery">
          <ul className={styles.list}>
            {cancelled.map((spin) => (
              <RewardRow key={spin.id} spin={spin} />
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}
